import { axiosInstance } from "@/utilities";
import { API_URL_LOGIN, API_URL_PROFILE } from "@/services/XCONS_ENV";
import { clearOperatorContactsCache } from "@/services/operator-contacts.cache";
import { vaciarTimelineCache } from "@/pages/conversations/timeline-cache";
import { notification } from "antd";
import type { AuthProvider } from "@refinedev/core";
import type { User } from "@/types/schema/schema.types";


/**
 * Espejo del perfil en `localStorage`.
 *
 * `getIdentity()` es asíncrono, pero **el menú lo necesita en el primer render**: se arma
 * fuera de `<Refine>` y no puede esperar un fetch (ver `usePerfilDelMenu`). Sin este espejo
 * la primera pasada dibuja el menú sin el perfil, y la sección que depende de un permiso no
 * aparece nunca — que es exactamente el bug de "Catálogo no está en el menú".
 *
 * No agrega exposición: es lo mismo que ya devuelve el API por `/auth/profile`, en la misma
 * máquina donde está el token. Y **no es fuente de verdad**: cada endpoint revalida los
 * permisos contra los que congeló la sesión.
 */
const CLAVE_PERFIL = "identity";

const leerPerfilGuardado = (): any => {
  try {
    const crudo = localStorage.getItem(CLAVE_PERFIL);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
};

/**
 * Aviso de que el perfil cambió: se cargó, se cerró sesión o entró otro usuario.
 *
 * El menú se arma **fuera de `<Refine>`** con un estado propio (`usePerfilDelMenu`) que sólo
 * se llenaba al montar la app. Cerrar sesión y volver a entrar no la vuelve a montar, así
 * que el menú seguía con el perfil del arranque: un permiso nuevo —el de Catálogo, por
 * ejemplo— no aparecía hasta un Ctrl+Shift+R.
 */
export const EVENTO_PERFIL = "xcons:perfil";
const avisarPerfil = () => {
  try {
    window.dispatchEvent(new Event(EVENTO_PERFIL));
  } catch {
    // Sin `window` (tests en node) no hay menú que rearmar.
  }
};

/** Olvida el perfil guardado. Lo usan el login y el logout: el de antes ya no vale. */
const olvidarPerfil = () => {
  cachedIdentity = null;
  identityPromise = null;
  try {
    localStorage.removeItem(CLAVE_PERFIL);
  } catch {
    // Sin storage no hay nada que olvidar.
  }
};

/** El perfil sin esperar nada, para quien no puede await: `null` si todavía no se pidió. */
export const perfilEnCache = (): any => cachedIdentity ?? leerPerfilGuardado();

let cachedIdentity: any = leerPerfilGuardado();
let identityPromise: Promise<any> | null = null;

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("access_token");

    if (tokenFromUrl) {
      localStorage.setItem("access_token", tokenFromUrl);
      localStorage.removeItem("smartMenuName");
      localStorage.removeItem("smartMenuState");
      localStorage.removeItem("smartMenuType");
      clearOperatorContactsCache();
      vaciarTimelineCache();
      // Un perfil de la sesión anterior (otro usuario, o permisos de antes) no puede
      // armar el menú de ésta: se olvida y el primer `getIdentity` trae el nuevo.
      olvidarPerfil();
      avisarPerfil();

      window.history.replaceState({}, document.title, "/");

      return {
        success: true,
        redirectTo: "/",
      };
    }
    
    try {
      const response = await fetch(API_URL_LOGIN, {
        method: "POST",
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();
      const { access_token } = data;

      localStorage.setItem("access_token", access_token);
      localStorage.removeItem("smartMenuName");
      localStorage.removeItem("smartMenuState");
      localStorage.removeItem("smartMenuType");
      clearOperatorContactsCache();
      vaciarTimelineCache();
      // Un perfil de la sesión anterior (otro usuario, o permisos de antes) no puede
      // armar el menú de ésta: se olvida y el primer `getIdentity` trae el nuevo.
      olvidarPerfil();
      avisarPerfil();

      return {
        success: true,
        redirectTo: "/",
      };
    } catch (e) {
      const error = e as Error;

      return {
        success: false,
        error: {
          message: error.message || "Login failed",
          name: "Invalid username or password",
        },
      };
    }
  },

  logout: async () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    olvidarPerfil();
    avisarPerfil();
    clearOperatorContactsCache();
    vaciarTimelineCache();

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
  const accessToken = localStorage.getItem("access_token");

  if (!accessToken) {
    // Si no hay token, pero ya estamos en /login, no hacemos nada
    if (window.location.pathname === "/login") {
      return {
        authenticated: false,
        // No redireccionamos, ya estamos en la ruta correcta
      };
    }

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  }

  try {
    const response = await fetch(API_URL_PROFILE, {
      method: "GET",
      headers: {
        accept: "*/*",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) throw new Error("Unauthorized");

    const data: User = await response.json();

    const hasPermissions = Boolean(
      Object.keys(data?.permissions?.seller || {}).length > 0 ||
      Object.keys(data?.permissions?.store || {}).length > 0
    );

    if (!hasPermissions) {
      localStorage.removeItem("access_token");

      notification.error({
        message: "Acceso denegado",
        description: "No posee ningún Store o Seller para acceder al panel.",
        duration: 10,
      });

      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }

    if (window.location.pathname === "/login") {
      return {
        authenticated: true,
        redirectTo: "/",
      };
    }

    return { authenticated: true };
  } catch (error) {
    localStorage.removeItem("access_token");

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  }
  },

   async getIdentity() {
    if (cachedIdentity) return cachedIdentity;
    if (identityPromise) return identityPromise;

    identityPromise = axiosInstance
      .get(API_URL_PROFILE)
      .then((res) => {
        cachedIdentity = res.data;
        try {
          localStorage.setItem(CLAVE_PERFIL, JSON.stringify(res.data));
        } catch {
          // Sin storage el menú sigue andando: se rearma cuando el fetch resuelve.
        }
        avisarPerfil();
        return cachedIdentity;
      })
      .finally(() => {
        identityPromise = null;
      });

    return identityPromise;
  },

  onError: async (error) => {
    if (error.statusCode === "UNAUTHENTICATED") {
      return {
        logout: true,
      };
    }

    return { error };
  },
};
